import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, RefreshCw, Save, Sliders, ShieldCheck, Users, Building2, Gift } from 'lucide-react';
import { getPortalConfig, updatePortalConfig } from '../../Services/GrowthService';
import { AccessTrack, PortalConfig } from '../../Types/GrowthType';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';

const labelClass = 'mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const ACCESS_TRACKS: AccessTrack[] = ['NORMAL', 'EARLY_ACCESS', 'CANDIDATE_REFERRAL', 'ADMIN_REFERRAL'];

const TRACK_WAITLIST_STATUS: Record<AccessTrack, string> = {
  NORMAL: 'UNDER_REVIEW',
  EARLY_ACCESS: 'EARLY_ACCESS_PENDING',
  CANDIDATE_REFERRAL: 'UNDER_REVIEW',
  ADMIN_REFERRAL: 'UNDER_REVIEW',
};

const TRACK_HELP: Record<AccessTrack, string> = {
  NORMAL: 'Standard review queue. Approve from user management to grant normal access.',
  EARLY_ACCESS: 'Founder member queue. Approval emails them as a founder member.',
  CANDIDATE_REFERRAL: 'Referred by a candidate. The referrer earns a profile visibility boost on approval.',
  ADMIN_REFERRAL: 'Sourced directly by the admin team. No bonus is ever granted.',
};

const defaultConfig: PortalConfig = {
  defaultAccessTrack: 'NORMAL',
  earlyAccessEnabled: true,
  candidateReferralEnabled: true,
  adminReferralEnabled: true,
  candidateInviteEnabled: true,
  recruiterInviteEnabled: true,
  lockRoleToInvite: true,
  candidateReferralBonusPoints: 10,
  adminReferralBonusPoints: 0,
  earlyAccessFounderLabel: 'Founder Member',
};

type ToggleProps = {
  name: keyof PortalConfig;
  label: string;
  help: string;
  value: boolean;
  disabled?: boolean;
  onChange: (name: keyof PortalConfig, value: boolean) => void;
};

const Toggle = ({ name, label, help, value, disabled, onChange }: ToggleProps) => (
  <label
    className={`flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3 transition ${
      disabled ? 'opacity-50' : 'hover:border-cyan-500/30'
    }`}
  >
    <input
      type="checkbox"
      checked={value}
      disabled={disabled}
      onChange={(e) => onChange(name, e.target.checked)}
      className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-500"
    />
    <span className="min-w-0">
      <span className="block text-sm font-bold text-slate-100">{label}</span>
      <span className="block text-[11px] font-semibold text-slate-500">{help}</span>
    </span>
  </label>
);

function PortalConfigComponent() {
  const [config, setConfig] = useState<PortalConfig>(defaultConfig);
  const [original, setOriginal] = useState<PortalConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPortalConfig();
      if (response.success && response.data) {
        setConfig({ ...defaultConfig, ...response.data });
        setOriginal({ ...defaultConfig, ...response.data });
      } else {
        showAlert('error', 'Load Error', response.message || 'Failed to load portal configuration.');
      }
    } catch {
      showAlert('error', 'Load Error', 'Failed to load portal configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const setValue = <K extends keyof PortalConfig>(name: K, value: PortalConfig[K]) =>
    setConfig((prev) => ({ ...prev, [name]: value }));

  const setToggle = (name: keyof PortalConfig, value: boolean) => {
    setConfig((prev) => {
      const next = { ...prev, [name]: value };
      // Admin referrals never pay a bonus, so keep the points pinned at 0.
      if (name === 'adminReferralEnabled' && !value) next.adminReferralBonusPoints = 0;
      if (name === 'candidateReferralEnabled' && !value) next.candidateReferralBonusPoints = 0;
      return next;
    });
  };

  const dirty = JSON.stringify(config) !== JSON.stringify(original);

  const handleSave = async () => {
    if (!config.lockRoleToInvite) {
      showAlert(
        'error',
        'Role Lock Required',
        'Role locking cannot be disabled. Every invitation must grant exactly one registration option.',
      );
      return;
    }
    if (!config.earlyAccessEnabled && config.defaultAccessTrack === 'EARLY_ACCESS') {
      showAlert('error', 'Invalid Combination', 'Early access is disabled but selected as the default track.');
      return;
    }
    setSaving(true);
    try {
      const response = await updatePortalConfig(config);
      if (response.success && response.data) {
        setConfig({ ...defaultConfig, ...response.data });
        setOriginal({ ...defaultConfig, ...response.data });
        showAlert('success', 'Saved', 'Portal configuration updated.');
      } else {
        showAlert('error', 'Save Error', response.message || 'Failed to save portal configuration.');
      }
    } catch {
      showAlert('error', 'Save Error', 'Failed to save portal configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Sliders size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Portal Config</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Waitlist tracks, invitation rules &amp; referral bonuses
            </p>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <button
            onClick={fetchConfig}
            title="Refresh"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !dirty}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          {/* Default waitlist track */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <header className="mb-4 flex items-center gap-2">
              <ShieldCheck size={16} className="text-cyan-400" />
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Default Waitlist Track</h2>
            </header>

            <div>
              <label className={labelClass}>Track for new registrations</label>
              <select
                value={config.defaultAccessTrack}
                onChange={(e) => setValue('defaultAccessTrack', e.target.value)}
                className={fieldClass}
              >
                {ACCESS_TRACKS.map((track) => (
                  <option key={track} value={track} disabled={track === 'EARLY_ACCESS' && !config.earlyAccessEnabled}>
                    {track} → {TRACK_WAITLIST_STATUS[track]}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                {TRACK_HELP[config.defaultAccessTrack as AccessTrack]}
              </p>
            </div>

            <div className="mt-5">
              <label className={labelClass}>Founder Member Label</label>
              <input
                value={config.earlyAccessFounderLabel}
                onChange={(e) => setValue('earlyAccessFounderLabel', e.target.value)}
                placeholder="Founder Member"
                className={fieldClass}
              />
              <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                Used in the early access approval email and on the registration screen.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3">
              <Toggle
                name="earlyAccessEnabled"
                label="Enable Early Access"
                help="Turns on the founder member track (EARLY_ACCESS_PENDING)."
                value={config.earlyAccessEnabled}
                onChange={setToggle}
              />
              <Toggle
                name="lockRoleToInvite"
                label="Lock Role To Invitation"
                help="Registration shows exactly one role option, taken from the invite. Required."
                value={config.lockRoleToInvite}
                onChange={setToggle}
              />
            </div>
          </section>

          {/* Invitation rules */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <header className="mb-4 flex items-center gap-2">
              <Users size={16} className="text-cyan-400" />
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Invitation Rules</h2>
            </header>

            <div className="grid grid-cols-1 gap-3">
              <Toggle
                name="candidateInviteEnabled"
                label="Allow Candidate Invitations"
                help="Admins can invite a candidate from the growth prospects screen."
                value={config.candidateInviteEnabled}
                onChange={setToggle}
              />
              <Toggle
                name="recruiterInviteEnabled"
                label="Allow Recruiter Invitations"
                help="Admins can invite a recruiter from the growth prospects screen."
                value={config.recruiterInviteEnabled}
                onChange={setToggle}
              />
            </div>

            <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Role compatibility</p>
              <ul className="mt-2 space-y-1.5 text-[11px] font-semibold text-slate-400">
                <li>
                  <span className="text-cyan-400">Candidate</span> · NORMAL, EARLY_ACCESS, CANDIDATE_REFERRAL, ADMIN_REFERRAL
                </li>
                <li>
                  <span className="text-fuchsia-400">Recruiter</span> · NORMAL, EARLY_ACCESS, ADMIN_REFERRAL
                </li>
              </ul>
              <p className="mt-2 text-[11px] font-semibold text-slate-600">
                CANDIDATE_REFERRAL is hidden for recruiters because the visibility boost only applies to candidate profiles.
              </p>
            </div>
          </section>

          {/* Referral bonuses */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <header className="mb-4 flex items-center gap-2">
              <Gift size={16} className="text-cyan-400" />
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Referral Bonuses</h2>
            </header>

            <div className="grid grid-cols-1 gap-3">
              <Toggle
                name="candidateReferralEnabled"
                label="Enable Candidate Referrals"
                help="Candidate referral codes boost the referrer's recruiter search position."
                value={config.candidateReferralEnabled}
                onChange={setToggle}
              />
              <Toggle
                name="adminReferralEnabled"
                label="Enable Admin Referrals"
                help="Admin-sourced registrations. Never awards a bonus."
                value={config.adminReferralEnabled}
                onChange={setToggle}
              />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Candidate Bonus Points</label>
                <input
                  type="number"
                  min={0}
                  value={config.candidateReferralBonusPoints}
                  disabled={!config.candidateReferralEnabled}
                  onChange={(e) => setValue('candidateReferralBonusPoints', Math.max(0, Number(e.target.value) || 0))}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Admin Bonus Points</label>
                <input
                  type="number"
                  min={0}
                  value={config.adminReferralBonusPoints}
                  disabled
                  className={`${fieldClass} opacity-50`}
                />
                <p className="mt-1.5 text-[11px] font-semibold text-slate-600">Always 0 by design.</p>
              </div>
            </div>
          </section>

          {/* Reference */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <header className="mb-4 flex items-center gap-2">
              <Building2 size={16} className="text-cyan-400" />
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Track Reference</h2>
            </header>

            <div className="space-y-2.5">
              {ACCESS_TRACKS.map((track) => (
                <div key={track} className="rounded-xl bg-slate-950/60 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-black uppercase tracking-widest text-cyan-300">{track}</span>
                    <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      {TRACK_WAITLIST_STATUS[track]}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] font-semibold text-slate-500">{TRACK_HELP[track]}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
              <p className="text-[11px] font-semibold text-amber-300/80">
                Approval always sets the user status to <span className="font-black">ACTIVE</span> and grants portal access.
              </p>
            </div>
          </section>
        </div>
      )}

      <AnimatePresence>
        {dirty && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
          >
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-cyan-400 shadow-[0_0_30px_-8px_rgba(34,211,238,0.5)] transition hover:bg-cyan-500/10 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Unsaved changes · Save
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PortalConfigComponent;
